import { i18nService, t } from '../../services/i18n';
import { describeDueDate, getRadarWindowEnd, getStartOfToday, getUrgencyClass, getUrgencyLabel, type RadarItem } from './property-shared';
import { escapeHtml, formatDate } from './property-shared';

export class RcPropertyRadarTab extends HTMLElement {
  items: RadarItem[] = [];

  render(): void {
    const monthStart = getStartOfToday();
    const urgentItems = this.items.filter((item) => item.urgency !== 'upcoming').slice(0, 3);
    const previewItems = this.items.slice(0, 4);
    const monthLabels = Array.from({ length: 12 }, (_, index) => {
      const date = new Date(monthStart);
      date.setMonth(date.getMonth() + index);
      return `<span>${date.toLocaleDateString(i18nService.dateLocale, { month: 'short' })}</span>`;
    }).join('');
    const overdueCount = this.items.filter((item) => item.urgency === 'overdue').length;
    const soonCount = this.items.filter((item) => item.urgency === 'soon').length;
    const upcomingCount = this.items.filter((item) => item.urgency === 'upcoming').length;
    const totalRange = Math.max(getRadarWindowEnd().getTime() - monthStart.getTime(), 1);

    const markers = this.items.map((item, index) => {
      const clamped = Math.min(Math.max(item.timestamp, monthStart.getTime()), getRadarWindowEnd().getTime());
      const left = ((clamped - monthStart.getTime()) / totalRange) * 100;
      const row = index % 2;
      return `
        <button class="expiry-radar-marker expiry-radar-marker--${item.kind}" data-action="open-tab" data-tab="${item.tab}" data-radar-item="${item.id}" style="left:${left}%;top:${44 + row * 16}px;" title="${escapeHtml(`${item.meta} - ${item.title}`)}">
          <span class="expiry-radar-dot ${item.urgency}"></span>
        </button>`;
    }).join('');

    this.innerHTML = `
      <section class="expiry-radar-card">
        <div class="expiry-radar-header">
          <div>
            <h3>${t('property.radar.title')}</h3>
            <p>${t('property.radar.subtitle')}</p>
          </div>
          <div class="expiry-radar-summary">
            <span class="badge badge-danger">${overdueCount} ${t('common.overdue').toLowerCase()}</span>
            <span class="badge badge-warning">${soonCount} ${t('common.soon').toLowerCase()}</span>
            <span class="badge badge-info">${upcomingCount} ${t('property.radar.upcoming').toLowerCase()}</span>
          </div>
        </div>
        ${this.items.length === 0
          ? `<div class="empty-state expiry-radar-empty"><p>${t('property.radar.empty')}</p></div>`
          : `
            <div class="expiry-radar-layout">
              <div class="expiry-radar-focus">
                <div class="expiry-radar-section-title">${t('property.radar.urgent')}</div>
                ${urgentItems.length === 0
                  ? `<div class="expiry-radar-muted">${t('property.radar.noUrgent')}</div>`
                  : `<div class="expiry-radar-urgent-list">
                      ${urgentItems.map((item) => `
                        <button class="expiry-radar-urgent-item" data-action="open-tab" data-tab="${item.tab}" data-radar-item="${item.id}">
                          <span class="expiry-radar-urgent-main">
                            <span class="expiry-radar-urgent-title">${escapeHtml(item.title)}</span>
                            <span class="expiry-radar-urgent-meta">${escapeHtml(item.meta)}</span>
                          </span>
                          <span class="expiry-radar-urgent-side">
                            <span class="badge ${getUrgencyClass(item.urgency)}">${getUrgencyLabel(item.urgency)}</span>
                            <span class="expiry-radar-row-date">${formatDate(item.dueDate)}</span>
                          </span>
                        </button>`).join('')}
                    </div>`}
              </div>
              <div class="expiry-radar-track-wrap">
                <div class="expiry-radar-section-title">${t('property.radar.timeline')}</div>
                <div class="expiry-radar-track">
                  <div class="expiry-radar-months">${monthLabels}</div>
                  <div class="expiry-radar-line"></div>
                  ${markers}
                </div>
              </div>
            </div>
            <div class="expiry-radar-preview">
              ${previewItems.map((item) => `
                <button class="expiry-radar-row" data-action="open-tab" data-tab="${item.tab}" data-radar-item="${item.id}">
                  <span class="expiry-radar-row-main">
                    <span class="expiry-radar-row-title">${escapeHtml(item.title)}</span>
                    <span class="expiry-radar-row-meta">${escapeHtml(item.meta)}</span>
                  </span>
                  <span class="expiry-radar-row-side">
                    <span class="badge ${getUrgencyClass(item.urgency)}">${getUrgencyLabel(item.urgency)}</span>
                    <span class="expiry-radar-row-date">${describeDueDate(item.dueDate)}</span>
                  </span>
                </button>`).join('')}
              ${this.items.length > previewItems.length ? `<span class="expiry-radar-more">+${this.items.length - previewItems.length} ${t('property.radar.moreItems')}</span>` : ''}
            </div>`}
      </section>
    `;
  }
}

if (!customElements.get('rc-property-radar-tab')) {
  customElements.define('rc-property-radar-tab', RcPropertyRadarTab);
}
